import { computed, defineComponent } from 'vue';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import { DataZoomComponent, TooltipComponent, LegendComponent, ToolboxComponent } from 'echarts/components';
use([
    LineChart,
    DataZoomComponent,
    TooltipComponent,
    LegendComponent,
    ToolboxComponent,
    CanvasRenderer
]);
import BaseEChart from './BaseEChart.vue';
import getOptionValue from 'src/store/preferences/getOptionValue';
import deepmerge from 'deepmerge';
export default defineComponent({
    components: { BaseEChart },
    props: {
        categoriesProp: { type: Array, default: null },
        xAxisDataProp: { type: Array, default: null },
        seriesProp: { type: Array, default: null },
        xAxisLabelProp: { type: String, default: 'X axis' },
        yAxisLabelProp: { type: String, default: 'Y axis' },
        yAxisNameGapProp: { type: Number, default: 20 },
        legendPlaceholderWidthProp: { type: String, default: '350px' },
        optionsToDeepmergeProp: { type: Object, default: () => ({}) },
        dataZoomProp: {
            type: Array,
            default: null
        },
        gridProp: {
            type: Object,
            default: () => ({
                top: '20px',
                left: '40px',
                right: '350px',
                bottom: '40px'
            })
        },
        legendProp: {
            type: Object,
            default: () => ({
                orient: 'vertical',
                align: 'left',
                top: 10,
                right: 10
            })
        },
        legendSelectorProp: {
            type: Array,
            default: () => [
                { type: 'all', title: 'All' },
                { type: 'inverse', title: 'Inverse' }
            ]
        },
        toolboxProp: {
            type: Object,
            default: () => ({
                show: true,
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    dataView: {
                        readOnly: false
                    },
                    magicType: {
                        type: ['line', 'bar']
                    },
                    restore: {},
                    saveAsImage: {}
                }
            })
        }
    },
    setup(props) {
        const legendSelector = computed(() => {
            var _a;
            if (((_a = series === null || series === void 0 ? void 0 : series.value) === null || _a === void 0 ? void 0 : _a.length) === 1)
                return null;
            return props.legendSelectorProp;
        });
        const series = computed(() => {
            if (!props.seriesProp)
                return undefined;
            if (!props.seriesProp[0])
                return undefined;
            return props.seriesProp.map((row) => ({
                data: row.data,
                type: 'line',
                stack: row.stack,
                areaStyle: row.areaStyle,
                lineStyle: row.lineStyle || { width: 2 },
                itemStyle: row.itemStyle,
                smooth: row.smooth !== undefined ? row.smooth : true,
                name: row.name,
                universalTransition: true,
                animationDuration: 1000
            }));
        });
        const axisCommonOptions = computed(() => {
            return {
                nameGap: 20,
                nameTextStyle: {
                    fontSize: 10
                },
                axisLabel: {
                    fontSize: 10,
                    show: getOptionValue(1) !== 'true' // i.e., not Disguise
                },
                splitLine: {
                    show: true
                }
            };
        });
        // eslint-disable-next-line no-unused-vars
        const option = computed(() => {
            var _a;
            const baseOption = {
                grid: Object.assign(Object.assign({}, props.gridProp), { right: props.legendPlaceholderWidthProp }),
                toolbox: Object.assign(Object.assign({}, props.toolboxProp), { right: props.legendPlaceholderWidthProp }),
                xAxis: Object.assign({ data: (props.categoriesProp || props.xAxisDataProp), type: 'category', name: props.xAxisLabelProp, nameLocation: 'middle' }, axisCommonOptions.value),
                yAxis: Object.assign(Object.assign({ type: 'value', name: props.yAxisLabelProp, nameLocation: 'middle' }, axisCommonOptions.value), { nameGap: props.yAxisNameGapProp }),
                series: series.value,
                legend: {
                    orient: props.legendProp.orient,
                    align: props.legendProp.align,
                    top: props.legendProp.top,
                    right: props.legendProp.right,
                    selector: legendSelector.value,
                    selectorPosition: 'end',
                    data: (_a = series.value) === null || _a === void 0 ? void 0 : _a.map(r => r.name),
                    textStyle: {
                        fontSize: 10
                    }
                },
                tooltip: {
                    trigger: 'axis'
                },
                dataZoom: props.dataZoomProp
            };
            const resultOption = deepmerge(baseOption, props.optionsToDeepmergeProp);
            return resultOption;
        });
        return { option };
    }
});
